import { getApiBaseUrl, fetchWithAuth } from '../common/api.js';
import { logout, getStoredSessionToken, decodeJwtPayload } from '../common/auth.js';
import { setProgress } from '../common/ui.js';
import { apiErrorMessage, displayError, initI18n, t } from '../common/i18n.js';
import {
    initPreferencesModule,
    type PreferencesModule,
    type PreferencesInfo,
} from './preferences.js';
import {
    initChannelContextModule,
    type ChannelContextAPI,
} from './channel-context.js';
import {
    initOptOutModule,
    type OptOutModule,
} from './opt-out.js';

// JWT payload structure
interface JWTPayload {
    userLogin: string;
    displayName?: string;
}

// DOM elements interface
interface ViewerElements {
    authStatus: HTMLElement | null;
    loadingOverlay: HTMLElement | null;
    loadingBar: HTMLElement | null;
    loggedInStatus: HTMLElement | null;
    loggedInUsername: HTMLElement | null;
    preferencesPanel: HTMLElement | null;
    logoutLink: HTMLAnchorElement | null;
}

// Application state interface
interface ViewerState {
    sessionToken: string | null;
    currentChannel: string | null;
    isAuthenticated: boolean;
}

// Services interface
interface ViewerServices {
    getSessionToken: () => string | null;
    setSessionToken: (tokenValue: string | null) => void;
    getCurrentChannel: () => string | null;
    setCurrentChannel: (channelName: string | null) => void;
}

// API response types
interface AuthStatusResponse {
    success: boolean;
    user?: {
        displayName?: string;
        userLogin?: string;
        login?: string;
    };
}

type AuthStatusType = 'info' | 'success' | 'error';

document.addEventListener('DOMContentLoaded', async () => {
  // Translate before anything renders. The catalog fetch was started at module
  // evaluation, so this await is nearly free -- and skipping it would let the
  // modules below build markup from `t()` against an empty catalog, which
  // renders bare keys.
  await initI18n();
    (async function bootstrap(): Promise<void> {
        const TEST_MODE = new URLSearchParams(window.location.search).has('test');
        const urlParams = new URLSearchParams(window.location.search);
        const initialChannel = urlParams.get('channel');

        const apiBaseUrl = getApiBaseUrl();

        const elements: ViewerElements = {
            authStatus: document.getElementById('auth-status'),
            loadingOverlay: document.getElementById('loading-overlay'),
            loadingBar: document.getElementById('loading-bar'),
            loggedInStatus: document.getElementById('logged-in-status'),
            loggedInUsername: document.getElementById('logged-in-username'),
            preferencesPanel: document.getElementById('preferences-panel'),
            logoutLink: document.getElementById('logout-link') as HTMLAnchorElement | null,
        };

        const state: ViewerState = {
            sessionToken: getStoredSessionToken(),
            currentChannel: initialChannel,
            isAuthenticated: false,
        };

        const services: ViewerServices = {
            getSessionToken: () => state.sessionToken,
            setSessionToken: (tokenValue: string | null) => {
                state.sessionToken = tokenValue;
                if (tokenValue) {
                    localStorage.setItem('app_session_token', tokenValue);
                } else {
                    localStorage.removeItem('app_session_token');
                }
            },
            getCurrentChannel: () => state.currentChannel,
            setCurrentChannel: (channelName: string | null) => {
                state.currentChannel = channelName;
            },
        };

        const channelContextModule: ChannelContextAPI = initChannelContextModule(
            { testMode: TEST_MODE },
            {
                setCurrentChannel: (channel: string | null) => {
                    state.currentChannel = channel;
                },
                getCurrentChannel: services.getCurrentChannel,
            },
            {
                onChannelChange: handleChannelChange,
            }
        );

        const preferencesModule: PreferencesModule = initPreferencesModule(
            { apiBaseUrl, testMode: TEST_MODE },
            { getCurrentChannel: (() => state.currentChannel || '') as () => string },
            {
                // Every successful load, with or without a channel, carries the
                // opt-out state, so this is the one place the card is filled. A
                // reply for a channel the viewer has since left is dropped: its
                // ignore status belongs to that channel, and the load for the
                // current one fills the card when it lands.
                onPreferencesLoaded: ({ channel, ignoreStatus, globalOptOut }: PreferencesInfo) => {
                    if (channel !== (state.currentChannel || null)) return;
                    optOutModule.update({ channel, ignoreStatus, globalOptOut });
                },
            }
        );

        const optOutModule: OptOutModule = initOptOutModule(
            { apiBaseUrl, testMode: TEST_MODE },
            { getCurrentChannel: services.getCurrentChannel },
        );

        if (elements.logoutLink) {
            elements.logoutLink.addEventListener('click', (e: Event) => {
                e.preventDefault();
                logout();
            });
        }

        const authenticated = await initializeAuth();
        if (!authenticated) {
            return;
        }

        showLoading();
        setProgress(elements.loadingBar, 0, 2);
        await preferencesModule.loadVoices();
        setProgress(elements.loadingBar, 1, 2);

        // Ensure initial channel context is reflected in UI
        if (state.currentChannel) {
            channelContextModule.setChannelUI(state.currentChannel);
        } else {
            channelContextModule.clearChannelUI();
        }

        await handleChannelChange(state.currentChannel);
        setProgress(elements.loadingBar, 2, 2);
        hideLoading();

        async function handleChannelChange(channelName: string | null): Promise<void> {
            if (channelName) {
                channelContextModule.setChannelUI(channelName);
            } else {
                channelContextModule.clearChannelUI();
            }

            try {
                // onPreferencesLoaded fills the opt-out card.
                await preferencesModule.loadPreferences();
                if (channelName) channelContextModule.showChannelResult(null);
            } catch (error) {
                // Without a response the opt-out state is unknown, so the card
                // hides, including a channel row left over from the previous
                // channel. The quiet reload below brings it back for the
                // every-channel toggle. The channel card says why it failed.
                optOutModule.clear();
                if (!channelName) return;
                channelContextModule.showChannelResult(displayError(error));
                // Drop the channel and fall back to global preferences. Kept,
                // it sent every save to PUT /preferences/<bad channel>, which
                // 404s, and left the last good channel's defaults and policy
                // (possibly every control disabled) under the failed name.
                state.currentChannel = null;
                try {
                    await preferencesModule.loadPreferences({ quiet: true });
                } catch (_) {
                    // Same cause as the failure above, which already showed a toast.
                }
            }
        }

        async function initializeAuth(): Promise<boolean> {
            if (TEST_MODE) {
                state.isAuthenticated = true;
                state.sessionToken = 'TEST_SESSION_TOKEN';
                showAuthStatus('', 'info');
                revealPreferencesPanel();
                if (elements.loggedInStatus) elements.loggedInStatus.style.display = '';
                if (elements.loggedInUsername) elements.loggedInUsername.textContent = t('msg.auth.testUser');
                if (state.currentChannel) {
                    channelContextModule.setChannelUI(state.currentChannel);
                }
                return true;
            }

            const urlParams = new URLSearchParams(window.location.search);
            const validatedParam = urlParams.get('validated');

            // Both handed over in memory by the head script of this page, which
            // already took them out of the URL before the third-party tag there
            // could run. The query reads only cover that script not having run.
            const globals = window as unknown as { __exchangeCode?: string; __sessionToken?: string };
            const exchangeCodeParam = globals.__exchangeCode || urlParams.get('code');
            const sessionTokenParam = globals.__sessionToken || urlParams.get('session_token');
            delete globals.__exchangeCode;
            delete globals.__sessionToken;
            // No `?error=` handling here on purpose: every OAuth failure now
            // redirects to auth-error.html, so nothing reaches this page with
            // an error to render.

            // The callback hands over a single-use code, not the token, so the
            // token never appears in the URL. Strip the code first: it is spent
            // by the exchange below, and should not survive in history either.
            if (exchangeCodeParam && validatedParam) {
                cleanAuthParamsFromUrl();
                return await redeemExchangeCode(exchangeCodeParam);
            }

            // The old shape, still accepted for anyone mid-flow across a deploy.
            if (sessionTokenParam && validatedParam) {
                try {
                    services.setSessionToken(sessionTokenParam);
                    let userDisplayName: string | null = null;
                    const payload = decodeJwtPayload<JWTPayload>(sessionTokenParam);
                    if (payload) {
                        localStorage.setItem('twitch_user_login', payload.userLogin);
                        userDisplayName = payload.displayName || payload.userLogin;
                    }
                    state.isAuthenticated = true;
                    revealPreferencesPanel();
                    showAuthStatus('', 'info');
                    if (elements.loggedInStatus) elements.loggedInStatus.style.display = '';
                    if (elements.loggedInUsername) elements.loggedInUsername.textContent = userDisplayName || t('msg.auth.genericUser');
                    if (state.currentChannel) {
                        channelContextModule.setChannelUI(state.currentChannel);
                    }
                    cleanAuthParamsFromUrl();
                    return true;
                } catch (error) {
                    console.error('Failed to process validated session token:', error);
                    showAuthStatus(t('msg.api.authFailed'), 'error');
                    return false;
                }
            }

            return await checkExistingSession();
        }

        /**
         * Removes the sign-in parameters from the address bar, leaving the
         * channel context in place. Keeps a spent code out of history.
         */
        function cleanAuthParamsFromUrl(): void {
            const cleanUrl = new URL(window.location.href);
            cleanUrl.searchParams.delete('code');
            cleanUrl.searchParams.delete('session_token');
            cleanUrl.searchParams.delete('validated');
            window.history.replaceState({}, '', cleanUrl.toString());
        }

        /**
         * Trades the one-time code from the callback for the session token and
         * establishes the viewer session.
         * @param code - The single-use code from the callback redirect
         * @return Whether a session was established
         */
        async function redeemExchangeCode(code: string): Promise<boolean> {
            try {
                const response = await fetch(`${apiBaseUrl}/auth/exchange`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ code }),
                });

                const data = await response.json().catch(() => ({}));
                if (!response.ok || !data.success || !data.session_token) {
                    throw new Error(apiErrorMessage(data, 'msg.auth.signInFailed'));
                }

                services.setSessionToken(data.session_token);

                let userDisplayName: string | null = null;
                const payload = decodeJwtPayload<JWTPayload>(data.session_token);
                if (payload) {
                    localStorage.setItem('twitch_user_login', payload.userLogin);
                    userDisplayName = payload.displayName || payload.userLogin;
                }

                state.isAuthenticated = true;
                revealPreferencesPanel();
                showAuthStatus('', 'info');
                if (elements.loggedInStatus) elements.loggedInStatus.style.display = '';
                if (elements.loggedInUsername) elements.loggedInUsername.textContent = userDisplayName || t('msg.auth.genericUser');
                if (state.currentChannel) {
                    channelContextModule.setChannelUI(state.currentChannel);
                }
                return true;
            } catch (error) {
                // A code is single-use, so reloading cannot retry this.
                console.error('Failed to redeem exchange code:', error);
                showAuthStatus(t('msg.auth.exchangeFailed'), 'error');
                return false;
            }
        }

        async function requireTwitchAuth(): Promise<boolean> {
            showAuthStatus(t('msg.auth.needViewerSignIn'), 'info');
            const loginButton = document.createElement('button');
            loginButton.textContent = t('msg.auth.signInWithTwitch');
            loginButton.className = 'btn btn-primary mt-2';
            // The server mints the state cookie and issues the redirect, so
            // this just navigates. The channel rides along as a query param
            // and the server stores it in the cookie for the callback.
            loginButton.onclick = () => {
                showAuthStatus(t('msg.auth.redirecting'), 'info');

                window.location.href = state.currentChannel
                    ? `${apiBaseUrl}/auth/twitch/viewer?channel=${encodeURIComponent(state.currentChannel)}`
                    : `${apiBaseUrl}/auth/twitch/viewer`;
            };
            if (elements.authStatus) {
                elements.authStatus.appendChild(loginButton);
            }
            return false;
        }

        async function checkExistingSession(): Promise<boolean> {
            const storedToken = services.getSessionToken();

            if (!storedToken) {
                return requireTwitchAuth();
            }

            try {
                const response = await fetchWithAuth(`${apiBaseUrl}/api/auth/status`);
                const data: AuthStatusResponse = await response.json();
                if (data.success === true && data.user) {
                    state.isAuthenticated = true;
                    revealPreferencesPanel();
                    showAuthStatus('', 'info');
                    if (elements.loggedInStatus) elements.loggedInStatus.style.display = '';
                    if (elements.loggedInUsername) {
                        elements.loggedInUsername.textContent = data.user.displayName || data.user.userLogin || data.user.login || t('msg.auth.genericUser');
                    }
                    return true;
                }
                throw new Error('Not authenticated');
            } catch (error) {
                console.error('Session check failed:', error);
                services.setSessionToken(null);
                return requireTwitchAuth();
            }
        }

        function showAuthStatus(message: string, type: AuthStatusType = 'info'): void {
            if (!elements.authStatus) return;
            if (message) {
                elements.authStatus.textContent = message;
                elements.authStatus.style.display = 'block';
                const klass = type === 'success' ? 'alert-success' : type === 'error' ? 'alert-danger' : 'alert-info';
                elements.authStatus.className = `alert ${klass} text-center`;
            } else {
                elements.authStatus.textContent = '';
                elements.authStatus.className = '';
                elements.authStatus.style.display = 'none';
            }
        }

        function showLoading(): void {
            if (elements.loadingOverlay) elements.loadingOverlay.style.display = '';
            if (elements.preferencesPanel) elements.preferencesPanel.style.display = 'none';
        }

        function hideLoading(): void {
            if (elements.loadingOverlay) elements.loadingOverlay.style.display = 'none';
            if (elements.preferencesPanel) elements.preferencesPanel.style.display = 'block';
        }

        function revealPreferencesPanel(): void {
            if (elements.preferencesPanel) {
                elements.preferencesPanel.style.display = 'block';
            }
        }
    })();
});
